import { Button, Card, Group, Skeleton, Stack, Text } from '@repletoryreach/sdk-ui';
import { Menu } from '@repletoryreach/sdk-ui/overlays';
import type { contacts } from '@repletoryreach/sdk-api';
import { useT } from '../../../i18n/I18nProvider';
import { formatDateTime } from '../../../lib/format';
import { ContactTags, OptInPill } from './ContactTag';
import { contactLabelOf } from './ContactAvatar';

export interface ContactCardsProps {
  rows: contacts.Contact[];
  loading: boolean;
  onOpen: (c: contacts.Contact) => void;
  /** The menu's View: the whole contact on its own page (8 October 2026). A tap on the name opens the drawer. */
  onOpenPage: (c: contacts.Contact) => void;
  onEdit: (c: contacts.Contact) => void;
  onDelete: (c: contacts.Contact) => void;
  /** Message this person. Kept in step with the table's own row action. */
  onMessage: (c: contacts.Contact) => void;
}

/**
 * The contact list as cards, below 820px.
 *
 * `contacts.md` "Responsive": the page had no CSS of its own, so a seven-column
 * table simply scrolled sideways inside the shell's `overflow-x: auto` and the
 * actions column sat two swipes off the right edge of a phone.
 */
export function ContactCards({ rows, loading, onOpen, onOpenPage, onEdit, onDelete, onMessage }: ContactCardsProps) {
  const t = useT();

  if (loading) {
    return (
      <Stack gap="sm">
        {[0, 1, 2, 3].map((i) => <Skeleton key={i} height={96} radius="md" />)}
      </Stack>
    );
  }

  return (
    <Stack gap="sm">
      {rows.map((c) => (
        <Card key={c.id} withBorder className="rr-contacts__card">
          <Stack gap={6}>
            <Group justify="space-between" align="flex-start" wrap="nowrap">
              <Stack gap={1} style={{ minWidth: 0 }}>
                <button type="button" className="rr-contacts__card-name" onClick={() => onOpen(c)}>
                  {contactLabelOf(c)}
                </button>
                {/* The number, the address, or both: an email-only contact
                    (4 October 2026) shows its address here. */}
                {c.msisdn && <Text role="caption" c="var(--rr-color-text-muted)">{c.msisdn}</Text>}
                {c.email && c.email !== contactLabelOf(c) && (
                  <Text role="caption" c="var(--rr-color-text-muted)">{c.email}</Text>
                )}
              </Stack>
              <Menu>
                <Menu.Target>
                  <Button size="xs" variant="subtle" aria-label={t('common.actions')}>⋯</Button>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item onClick={() => onOpenPage(c)}>{t('action.view')}</Menu.Item>
                  <Menu.Item onClick={() => onEdit(c)}>{t('action.edit')}</Menu.Item>
                  {c.msisdn && <Menu.Item onClick={() => onMessage(c)}>{t('contacts.actions.message')}</Menu.Item>}
                  <Menu.Divider />
                  <Menu.Item color="red" onClick={() => onDelete(c)}>{t('contacts.remove.action')}</Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </Group>

            <Group gap={6} wrap="wrap">
              <OptInPill optedIn={c.opt_in} />
              <ContactTags tags={c.tags ?? []} />
            </Group>

            <Text role="caption" c="var(--rr-color-text-muted)">
              {t('common.updated')}: {formatDateTime(c.updated_at)}
            </Text>
          </Stack>
        </Card>
      ))}
    </Stack>
  );
}
